/**
 * UpdateBanner — Banner non-bloccante "Nuova versione disponibile".
 *
 * Logica:
 *  - Al mount fetcha `/sw.js` (no-cache) e legge `CACHE_VERSION = "haccp-vXX"`
 *  - Memorizza la versione iniziale
 *  - Polling ogni 5 minuti: re-fetcha e confronta. Se diversa → mostra banner.
 *  - Banner discreto in basso-centro (sopra MobileBottomNav), dismissibile.
 *  - Click "Aggiorna" → `window.location.reload()` (il SW ha già skipWaiting+claim
 *    quindi il refresh prende subito la nuova versione).
 *
 * NB: usa CACHE_VERSION di sw.js come fonte di verità (bump-ato ad ogni release),
 * non APP_BUILD_ID env (non sempre aggiornato).
 */
import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

const POLL_MS = 5 * 60 * 1000; // 5 min
const SW_URL = "/sw.js";
const VERSION_RE = /CACHE_VERSION\s*=\s*["']([^"']+)["']/;

async function fetchSwVersion() {
  try {
    const res = await fetch(SW_URL, { cache: "no-store" });
    if (!res.ok) return null;
    const txt = await res.text();
    const m = VERSION_RE.exec(txt);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

export default function UpdateBanner() {
  const initialVersion = useRef(null);
  const [newVersion, setNewVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Carica versione iniziale (1° mount)
    fetchSwVersion().then((v) => {
      if (cancelled) return;
      initialVersion.current = v;
    });

    // Polling ogni 5 min
    const id = setInterval(async () => {
      if (cancelled) return;
      const v = await fetchSwVersion();
      if (!v || !initialVersion.current) return;
      if (v !== initialVersion.current) {
        setNewVersion(v);
      }
    }, POLL_MS);

    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!newVersion || dismissed) return null;

  return (
    <div
      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100%-2rem)] print:hidden"
      role="status"
      aria-live="polite"
      data-testid="update-banner"
    >
      <div className="bg-stone-900 text-white rounded-2xl shadow-xl ring-1 ring-white/10 px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-400 text-stone-900 flex items-center justify-center shrink-0 shadow">
          <RefreshCw className="w-4 h-4" strokeWidth={2.5}/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Nuova versione disponibile</p>
          <p className="text-[11px] text-stone-300 leading-tight">Aggiorna per ottenere le ultime migliorie.</p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="shrink-0 h-9 px-3 rounded-xl bg-amber-400 text-stone-900 text-xs font-bold hover:bg-amber-300 active:scale-95 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          data-testid="update-banner-reload"
        >
          Aggiorna
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 w-8 h-8 rounded-lg text-stone-400 hover:text-white hover:bg-white/10 inline-flex items-center justify-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          aria-label="Chiudi"
          data-testid="update-banner-dismiss"
        >
          <X className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}
